import type { IconCategory } from '@warplan/shared';
import { ICON_CATEGORIES } from '@warplan/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AssetGroupKey =
  | 'builds'
  | 'targets'
  | 'facilities'
  | 'hubs'
  | 'msup'
  | 'navy'
  | 'op_info'
  | 'op_attack'
  | 'op_defend'
  | 'op_arty'
  | 'op_build'
  | 'op_navy'
  | 'op_unit'
  | 'op_vehicle';

type GroupMap<T> = Record<AssetGroupKey, T>;

/**
 * Per-category visibility of arrows. When `false`, arrows of that category
 * are hidden from the interactive map and pre-filtered out of exports.
 */
export type ArrowVisibilityMap = Record<IconCategory, boolean>;

interface AssetPanelState {
  isOpen: boolean;
  groupOpen: GroupMap<boolean>;
  /** Per-kind visibility for arrows (rendering + export pre-filter). */
  arrowVisibility: ArrowVisibilityMap;
  toggle: () => void;
  setOpen: (open: boolean) => void;
  toggleGroup: (group: AssetGroupKey) => void;
  toggleArrowVisibility: (kind: IconCategory) => void;
}

const DEFAULT_OPEN: GroupMap<boolean> = {
  builds: true,
  targets: true,
  facilities: true,
  hubs: true,
  msup: true,
  navy: true,
  op_info: true,
  op_attack: true,
  op_defend: true,
  op_arty: true,
  op_build: true,
  op_navy: true,
  op_unit: true,
  op_vehicle: true,
};

const DEFAULT_ARROW_VISIBILITY: ArrowVisibilityMap = ICON_CATEGORIES.reduce(
  (acc, k) => {
    acc[k] = true;
    return acc;
  },
  {} as ArrowVisibilityMap,
);

function sanitizeArrowVisibility(input: unknown): ArrowVisibilityMap {
  const out = { ...DEFAULT_ARROW_VISIBILITY };
  if (input && typeof input === 'object') {
    for (const k of ICON_CATEGORIES) {
      const v = (input as Record<string, unknown>)[k];
      if (typeof v === 'boolean') out[k] = v;
    }
  }
  return out;
}

export const useAssetPanel = create<AssetPanelState>()(
  persist(
    (set) => ({
      isOpen: true,
      groupOpen: DEFAULT_OPEN,
      arrowVisibility: DEFAULT_ARROW_VISIBILITY,
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),
      setOpen: (open) => set({ isOpen: open }),
      toggleGroup: (group) =>
        set((s) => ({
          groupOpen: { ...s.groupOpen, [group]: !s.groupOpen[group] },
        })),
      toggleArrowVisibility: (kind) =>
        set((s) => ({
          arrowVisibility: { ...s.arrowVisibility, [kind]: !s.arrowVisibility[kind] },
        })),
    }),
    {
      name: 'warplan.asset-panel',
      version: 6,
      partialize: (s) => ({
        isOpen: s.isOpen,
        groupOpen: s.groupOpen,
        arrowVisibility: s.arrowVisibility,
      }),
      migrate: (persisted) => {
        const p = (persisted ?? {}) as Partial<AssetPanelState> & { sortMode?: unknown };
        // Drop the obsolete sortMode field from earlier versions.
        const { sortMode: _unused, ...rest } = p;
        // Merge persisted groupOpen with defaults so any newly-added group key
        // (e.g. operation-mode sections) starts open by default.
        const mergedGroupOpen = { ...DEFAULT_OPEN, ...(rest.groupOpen ?? {}) };
        return {
          isOpen: rest.isOpen ?? true,
          groupOpen: mergedGroupOpen,
          arrowVisibility: sanitizeArrowVisibility(rest.arrowVisibility),
        };
      },
    },
  ),
);
